import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Database, Trash2, Plus, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Preference {
  key: string;
  value: any;
  confidence: number;
  created_at: string;
}

export default function MemoryManager() {
  const { toast } = useToast();
  const { orgId } = useAuth();
  const [preferences, setPreferences] = useState<Preference[]>([]);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    if (orgId) loadMemory();
  }, [orgId]);

  const loadMemory = async () => {
    if (!orgId) return;
    setLoading(true);

    // Load preferences
    const { data: prefData } = await supabase.functions.invoke('agent-memory-service', {
      body: {
        action: 'get_preferences',
        data: { org_id: orgId }
      }
    });

    if (prefData?.preferences) {
      const prefs = Object.entries(prefData.preferences).map(([key, value]: [string, any]) => ({
        key,
        value: value.value,
        confidence: value.confidence,
        created_at: new Date().toISOString()
      }));
      setPreferences(prefs);
    }

    // Load patterns
    const { data: patternData } = await supabase.functions.invoke('agent-memory-service', {
      body: {
        action: 'get_patterns',
        data: { org_id: orgId }
      }
    });

    if (patternData?.patterns) {
      setPatterns(patternData.patterns);
    }
    
    setLoading(false);
  };

  const handleAddPreference = async () => {
    if (!orgId || !newKey || !newValue) return;

    try {
      const { error } = await supabase.functions.invoke('agent-memory-service', {
        body: {
          action: 'set_preference',
          data: {
            org_id: orgId,
            key: newKey,
            value: newValue,
            confidence: 0.9
          }
        }
      });

      if (error) throw error;

      setNewKey('');
      setNewValue('');
      loadMemory();
      
      toast({
        title: 'Preference Added',
        description: 'New preference has been stored',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add preference',
        variant: 'destructive',
      });
    }
  };

  const handleDeletePreference = async (key: string) => {
    // Implementation would require a delete endpoint in agent-memory-service
    toast({
      title: 'Delete Feature',
      description: 'Delete endpoint needs to be implemented',
      variant: 'destructive',
    });
  };

  return (
    <div className="space-y-6">
      {/* Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Agent Preferences
          </CardTitle>
          <CardDescription>
            Learned preferences and configurations that shape Bridgette's behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Preference key (e.g., response_style)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
            <Input
              placeholder="Value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
            <Button onClick={handleAddPreference} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add
            </Button>
            <Button onClick={loadMemory} variant="outline" className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="space-y-2">
            {preferences.map((pref) => (
              <div
                key={pref.key}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{pref.key}</span>
                    <Badge variant="secondary" className="text-xs">
                      {(pref.confidence * 100).toFixed(0)}% confidence
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {JSON.stringify(pref.value)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeletePreference(pref.key)}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Patterns */}
      <Card>
        <CardHeader>
          <CardTitle>Learned Patterns</CardTitle>
          <CardDescription>
            Behavioral patterns detected from past interactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {patterns.length === 0 ? (
            <p className="text-sm text-muted-foreground">No patterns learned yet</p>
          ) : (
            <div className="space-y-2">
              {patterns.map((pattern, idx) => (
                <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{pattern.pattern_type}</span>
                    <Badge>{pattern.occurrence_count} occurrences</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {JSON.stringify(pattern.pattern_data)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
